import { useState, useMemo } from 'react';

const ACTIVITY_ICONS = {
  taskComplete: { bg: "bg-green-100", color: "text-green-600", path: <path d="M20 6L9 17l-5-5" strokeWidth="3" /> },
  taskStatus: { bg: "bg-blue-100", color: "text-blue-600", path: <><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></> },
  taskBlocked: { bg: "bg-red-100", color: "text-red-500", path: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></> },
  dailyLog: { bg: "bg-blue-100", color: "text-blue-600", path: <><path d="M12 5v14M5 12h14" /></> },
  changeOrder: { bg: "bg-amber-100", color: "text-amber-600", path: <><path d="M12 9v4M12 17h.01" /><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></> },
  coApproved: { bg: "bg-green-100", color: "text-green-600", path: <><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" /></> },
  fileUpload: { bg: "bg-emerald-100", color: "text-emerald-600", path: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></> },
  memberAdded: { bg: "bg-purple-100", color: "text-purple-600", path: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" /></> },
};

function deriveActivities(projects) {
  const activities = [];

  for (const p of projects) {
    for (const t of (p.tasks || [])) {
      if (t.isPhaseHeader) continue;
      if (t.dateCompleted) {
        activities.push({
          id: `task-complete-${p.id}-${t.id}`,
          type: "taskComplete",
          date: new Date(t.dateCompleted),
          projectId: p.id,
          projectName: p.name,
          taskId: t.id,
          primary: <><span className="font-medium">{t.name}</span> marked as <span className="text-green-600 font-medium">Complete</span></>,
          dest: "Task Detail",
          navType: "task-detail",
        });
      }
      if (t.status === "Blocked" && t.startDate) {
        activities.push({
          id: `task-blocked-${p.id}-${t.id}`,
          type: "taskBlocked",
          date: new Date(t.startDate),
          projectId: p.id,
          projectName: p.name,
          taskId: t.id,
          primary: <><span className="font-medium">{t.name}</span> marked as <span className="text-red-600 font-medium">Blocked</span>{t.notes ? ` — ${t.notes.slice(0, 60)}` : ""}</>,
          dest: "Task Detail",
          navType: "task-detail",
        });
      }
      if (t.status === "In Progress" && t.startDate) {
        activities.push({
          id: `task-progress-${p.id}-${t.id}`,
          type: "taskStatus",
          date: new Date(t.startDate),
          projectId: p.id,
          projectName: p.name,
          taskId: t.id,
          primary: <><span className="font-medium">{t.name}</span> moved to <span className="text-blue-600 font-medium">In Progress</span></>,
          dest: "Task Detail",
          navType: "task-detail",
        });
      }
    }

    for (const co of (p.changeOrders || [])) {
      const coDate = co.updatedAt || co.createdAt;
      if (!coDate) continue;
      if (co.status === "Approved") {
        activities.push({
          id: `co-approved-${p.id}-${co.id}`,
          type: "coApproved",
          date: new Date(coDate),
          projectId: p.id,
          projectName: p.name,
          primary: <>Change order <span className="font-medium">{co.title || co.id}</span> <span className="text-green-600 font-medium">approved</span>{co.amount ? ` — ${co.amount >= 0 ? "+" : ""}$${Math.abs(co.amount).toLocaleString()}` : ""}</>,
          dest: "Change Order",
          navType: "change-order",
        });
      } else if (co.status === "Submitted") {
        activities.push({
          id: `co-submitted-${p.id}-${co.id}`,
          type: "changeOrder",
          date: new Date(coDate),
          projectId: p.id,
          projectName: p.name,
          primary: <><span className="font-medium">{co.title || co.id}</span> submitted{co.amount ? ` — ${co.description || ""}${co.amount >= 0 ? " (+" : " ("}$${Math.abs(co.amount).toLocaleString()})` : ""}</>,
          dest: "Change Order",
          navType: "change-order",
        });
      }
    }

    for (const log of (p.dailyLogs || [])) {
      activities.push({
        id: `log-${p.id}-${log.id}`,
        type: "dailyLog",
        date: new Date(log.date || log.createdAt),
        projectId: p.id,
        projectName: p.name,
        primary: <><span className="font-medium">Daily log</span> recorded{log.weather ? ` — ${log.weather}` : ""}{log.crewSize ? `, crew of ${log.crewSize}` : ""}</>,
        dest: "Daily Log",
        navType: "daily-log",
      });
    }
  }

  activities.sort((a, b) => b.date - a.date);
  return activities;
}

function groupByPeriod(activities, mode) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const groups = [];
  let currentLabel = null;
  let currentItems = [];

  function getLabel(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const diff = Math.round((today - d) / (1000 * 60 * 60 * 24));

    if (mode === "day") {
      if (diff === 0) return `Today · ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
      if (diff === 1) return `Yesterday · ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
      return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
    }
    // week mode
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfLastWeek = new Date(startOfWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

    if (d >= startOfWeek) return "This Week";
    if (d >= startOfLastWeek) return "Last Week";
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - d.getDay());
    return `Week of ${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }

  for (const item of activities) {
    const label = getLabel(item.date);
    if (label !== currentLabel) {
      if (currentLabel !== null) groups.push({ label: currentLabel, items: currentItems });
      currentLabel = label;
      currentItems = [];
    }
    currentItems.push(item);
  }
  if (currentLabel !== null) groups.push({ label: currentLabel, items: currentItems });
  return groups;
}

function timeAgo(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function DashboardActivity({ projects, onNavigate }) {
  const [mode, setMode] = useState("week");
  const [limit, setLimit] = useState(20);

  const allActivities = useMemo(() => deriveActivities(projects), [projects]);
  const visibleActivities = allActivities.slice(0, limit);
  const groups = useMemo(() => groupByPeriod(visibleActivities, mode), [visibleActivities, mode]);

  function handleClick(item) {
    if (item.navType === "task-detail") onNavigate("task-detail", item.projectId, item.taskId);
    else if (item.navType === "change-order") onNavigate("change-order", item.projectId);
    else if (item.navType === "daily-log") onNavigate("daily-log", item.projectId);
    else if (item.navType === "files") onNavigate("files", item.projectId);
    else if (item.navType === "team-member") onNavigate("contacts");
  }

  if (allActivities.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <ToggleButtons mode={mode} onChange={setMode} />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-slate-400 text-sm">No activity yet. Changes to tasks, change orders, and daily logs will appear here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 mb-2">
        <ToggleButtons mode={mode} onChange={setMode} />
      </div>
      {groups.map(group => (
        <div key={group.label}>
          <div className="sticky top-0 bg-slate-50 py-2 z-10">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{group.label}</p>
          </div>
          {group.items.map(item => {
            const icon = ACTIVITY_ICONS[item.type] || ACTIVITY_ICONS.taskStatus;
            return (
              <button key={item.id} onClick={() => handleClick(item)}
                className="bg-white rounded-lg border border-slate-200 px-4 py-3 flex items-start gap-3 w-full text-left cursor-pointer hover:bg-slate-50 transition-colors mb-1 group">
                <div className={`w-7 h-7 rounded-full ${icon.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <svg className={`w-3.5 h-3.5 ${icon.color}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    {icon.path}
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-stone-800">{item.primary}</p>
                  <p className="text-xs text-slate-400">
                    {item.projectName} · {timeAgo(item.date)}
                    <span className="text-[10px] font-semibold text-amber-500 bg-amber-50 px-1 rounded hidden group-hover:inline ml-1">→ {item.dest}</span>
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      ))}
      {allActivities.length > limit && (
        <div className="text-center py-4">
          <button onClick={() => setLimit(l => l + 20)}
            className="text-xs text-amber-600 font-medium hover:text-amber-700">Load earlier activity →</button>
        </div>
      )}
    </div>
  );
}

function ToggleButtons({ mode, onChange }) {
  return (
    <div className="flex gap-0.5 border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => onChange("week")}
        className={`text-xs px-3 py-1.5 transition-colors ${mode === "week" ? "bg-amber-500 text-white" : "bg-white text-slate-500 hover:text-slate-700"}`}>
        Week
      </button>
      <button onClick={() => onChange("day")}
        className={`text-xs px-3 py-1.5 transition-colors ${mode === "day" ? "bg-amber-500 text-white" : "bg-white text-slate-500 hover:text-slate-700"}`}>
        Day
      </button>
    </div>
  );
}
